import { ExternalAssetSource, MediaType } from "../../types";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams } from "../base/types";
import { FontAsset } from "./assets";
import { FontsourceDetailItem, FontsourceListItem } from "./types";

/**
 * Fontsource Provider
 *
 * Provides read-only access to Fontsource's library of open-source fonts.
 * https://fontsource.org/
 *
 * API documentation: https://fontsource.org/docs/api
 * No API key required.
 *
 * The full font list is fetched once and cached client-side; search and
 * pagination are applied locally, matching the reference app behaviour
 * (server-side filter mode is disabled for FontSource).
 */
export class FontsourceProvider extends BaseProvider {
  private static readonly API_BASE = 'https://api.fontsource.org/v1/fonts';

  /** Filtered-out font families that have rendering issues (mirrors the reference app) */
  private static readonly FILTERED_FAMILIES = [
    'Material Icons Two Tone',
    'Noto Emoji',
    'Noto Color Emoji',
  ];

  private cachedList: FontAsset[] | null = null;

  constructor() {
    super({
      id: ExternalAssetSource.Fontsource,
      name: 'fontsource',
      displayName: 'Fontsource',
      description: 'Open-source fonts from Fontsource',
      baseUrl: FontsourceProvider.API_BASE,
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [MediaType.Font],
      },
    });
  }

  /** Fetch and cache the full font list. */
  private async fetchAllFonts(): Promise<FontAsset[]> {
    if (this.cachedList) return this.cachedList;

    try {
      const data = await this.fetchJson<FontsourceListItem[]>(this.baseUrl);
      this.cachedList = data
        .filter(item => !FontsourceProvider.FILTERED_FAMILIES.includes(item.family))
        .map(item => new FontAsset(item, this.baseUrl));
      return this.cachedList;
    } catch (error) {
      console.error('Failed to fetch Fontsource font list:', error);
      return [];
    }
  }

  /**
   * Search fonts in Fontsource.
   * Filtering, sorting and pagination are all client-side.
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const all = await this.fetchAllFonts();

    let filtered = all;

    if (params.query) {
      const q = params.query.toLowerCase();
      filtered = all.filter(asset => asset.getName().toLowerCase().includes(q));
    }

    // Apply category filter via sort param reused as category key (matches reference usage)
    // Consumers can pass params.sort = 'popularity' | 'alpha' | 'date' | 'trending'
    // Fontsource list API doesn't support server-side sort, so we mirror behaviour
    // of the reference app which ignores sort for client-side list.

    const page = params.page ?? 1;
    const limit = params.limit;
    if (limit) {
      const start = (page - 1) * limit;
      return filtered.slice(start, start + limit);
    }

    return filtered;
  }

  /**
   * Get a specific font by its Fontsource ID.
   * Fetches the detail endpoint which includes variant URLs.
   */
  async getAssetById(id: string): Promise<IAsset> {
    try {
      const detail = await this.fetchJson<FontsourceDetailItem>(
        `${this.baseUrl}/${encodeURIComponent(id)}`
      );
      return new FontAsset(detail, this.baseUrl);
    } catch (error) {
      throw new Error(`Font with ID "${id}" not found in Fontsource`);
    }
  }
}
